import { CSSProperties, useEffect, useRef } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { audioPath, OPPONENT_ACTION_VOLUME } from 'constants/audioConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import useSound from 'hooks/useSound';

type WinnerMsgPropType = {
  backgroundColor?: string;
  message?: string;
  /**
   * Whether the win cue may sound. Unity plays PLAYERWINS from the winner's own
   * UIPlayer with isOwnSound = IsPOVPlayer() (UIPlayer.cs:825), so a win by
   * ANOTHER player is silent while "Mute Other Players' Sounds" is on. The
   * caller owns that decision because only it knows whose seat this is.
   *
   * Defaults true so the current-player render path (PortraitCurrentPlayer)
   * keeps sounding without change — that pill is always the POV player's.
   */
  soundEnabled?: boolean;
  /** This seat is the viewer's own, so the win cue crosses tables. */
  isOwnWin?: boolean;
  /** Table id, so the cross-table claim is scoped per table. */
  gameID?: string;
};

export const WinnerMsg = ({
  backgroundColor = '#628B48',
  message = 'Winner',
  soundEnabled = true,
  isOwnWin = false,
  gameID = ''
}: WinnerMsgPropType) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const isHandHistorySnapshot = useHandHistorySnapshotMode();

  // WINNING IS A TURN-BOUNDARY CUE when it is YOUR win. Go all in, switch to
  // another table to wait it out, and the hand resolving in your favour has to
  // reach you there — it is the end of your turn, same as your fold or your
  // call (owner, 8/26). Somebody else's win is ambient table sound and stays on
  // the table being watched.
  // Dedup is useSound's job, not this component's: it mounts AT the moment of
  // the win, so its audio is still resolving on first render and a claim taken
  // here would be spent on a call that cannot play (see WrapperOptions.dedupeKey).
  const [playOwnWin] = useSound(audioPath.playerWins, {
    crossTable: true,
    dedupeKey: `win:${gameID}`
  });
  // SOMEBODY ELSE'S WIN is background sound (EN-10585 AC2, dev review #3747):
  // table-local, and attenuated like every other opponent action cue — with the
  // mute default now OFF this is the first time opponents' wins are audible at
  // all, and player_wins sits in the OUTCOMES tier of the loudness ladder.
  // The dedupe key folds a split pot into ONE cue: SeatWithPlayer mounts one
  // WinnerMsg per winning seat, all in the same commit, so a three-way chop
  // would otherwise play three overlapping copies. Keying on the gameID alone
  // is per-resolution-moment, not once-ever — claims expire on claimCue's
  // ~400ms window (the same reason `win:` above works across hands): the N
  // simultaneous copies land in one window, the next hand's win is a fresh one.
  // `otherwin:` is deliberately NOT `win:`, so a chop that includes the
  // viewer's own seat can never let this attenuated local play swallow the
  // cross-table own-win cue.
  const [playOtherWin] = useSound(audioPath.playerWins, {
    volume: OPPONENT_ACTION_VOLUME,
    dedupeKey: `otherwin:${gameID}`
  });

  useEffect(() => {
    if (isHandHistorySnapshot || !soundEnabled) return;
    if (isOwnWin) playOwnWin();
    else playOtherWin();
  }, [isHandHistorySnapshot, playOwnWin, playOtherWin, soundEnabled, isOwnWin]);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    if (isHandHistorySnapshot) {
      gsap.set(el, { opacity: 1, scale: 1, y: -10 });
      return;
    }

    const tl = gsap.timeline();

    // EN-10520 — grow in from 0.9, not from nothing. Popping from scale 0 read
    // as an abrupt appearance and the 1.15 overshoot made the badge lunge over
    // whatever sat above the seat; starting near full size and overshooting a
    // hair keeps the badge unmistakable while staying inside its own lane.
    tl.fromTo(
      el,
      { scale: 0.9, opacity: 0, y: 10 },
      { scale: 1.04, opacity: 1, y: -10, duration: 0.3, ease: 'back.out(1.2)' }
    )
      // Settle to final size
      .to(el, { scale: 1, duration: 0.2, ease: 'power2.out' })
      // Gold shimmer pulse
      .to(el, {
        boxShadow: '0 0 20px 6px rgba(255, 215, 0, 0.6)',
        duration: 0.4,
        ease: 'sine.inOut',
        repeat: 2,
        yoyo: true
      })
      // Hold
      .to(el, { duration: 1.5 })
      // Fade out
      .to(el, { opacity: 0, y: -20, scale: 0.9, duration: 0.3, ease: 'power2.in' });

    return () => {
      tl.kill();
    };
  }, [isHandHistorySnapshot]);

  return (
    <div style={containerStyle}>
      <div ref={elementRef} style={{ ...badgeStyle, backgroundColor }}>
        <EmojiEventsIcon style={{ fontSize: '1rem', color: '#FFD700' }} />
        <span style={textStyle}>{message}</span>
      </div>
    </div>
  );
};

const containerStyle: CSSProperties = {
  position: 'absolute',
  top: -40,
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  zIndex: 10,
  pointerEvents: 'none'
};

const badgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.3rem 0.75rem',
  borderRadius: '20px',
  border: '1px solid rgba(255, 215, 0, 0.4)',
  boxShadow: '0 0 12px 3px rgba(255, 215, 0, 0.3)'
};

const textStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};
